import $ from 'jquery';
import * as React from 'react';

import { getDisplayName } from 'components/hoc';
import { LayoutTriggers } from 'defs/layout';
import noop from 'helper/noop';
import { delayed } from 'lib/Job';

import { TerrificActionEnumType } from 'components/hoc/Cards/withTerrificBridge';
import { WithTrackerPropsType } from 'components/hoc/Cards/withTracker';
import { WithLayoutPropsType } from 'components/hoc/withLayout';
import { TUTORIAL_ID } from 'types/Tutorial';

import { CardTypeTypes } from 'types/Card';

export type UiStateTypes = 'feed' | 'folded' | 'reopened' | 'detail';

export type WithUiStateProxyPropsType = {
	doTriggerTutorial: any;
	state: UiStateTypes;
	type: CardTypeTypes;
} & WithTrackerPropsType &
	WithLayoutPropsType &
	WithTrackerPropsType;

export const UiStates = {
	Feed: 'feed',
	Folded: 'folded',
	Reopened: 'reopened',
	Detail: 'detail',
};

export type UiStateType = {
	ui: UiStateTypes;
};

export type WithUiStatePropsType = {
	uiState: UiStateTypes;
	onBackFromDetail: () => void;
	onClose: () => void;
	onCloseOverlay: () => void;
	onCloseOverlayArticle: (event: Event) => void;
	onCardContentClick: () => void;
	onScrollToCard: () => void;
	onShowDetailsClick: () => void;
	onSetCardUIFolded: () => void;
	onCompleteCard: (callback?: Function) => void;
	childComponentRef: () => void;
};

export type WithUiStateStateType = {
	ui: UiStateTypes;
};

export default function withUiState<P extends object>(WrappedComponent: React.ComponentType<P>) {
	return class UIStateProxy extends React.Component<P & WithUiStateProxyPropsType, WithUiStateStateType> {
		public static displayName = `withUiState(${getDisplayName(WrappedComponent)})`;

		public childComponent;

		public state: WithUiStateStateType = {
			ui: this.props.state,
		};

		public forceFireTerrificAction: (action: TerrificActionEnumType) => void = undefined as any;

		public componentDidMount() {
			this.forceFireTerrificAction = (action: TerrificActionEnumType) => {
				this.childComponent.fireTerrificAction(action);
			};
		}

		public componentWillUnmount() {
			this.forceFireTerrificAction = noop;
		}

		public componentDidUpdate = (prevProps) => {
			if (prevProps.state !== UiStates.Folded && this.props.state === UiStates.Folded) {
				this.setState({ ui: UiStates.Folded } as any);
			}
		};

		public handleBackFromDetail = () => {
			const { state } = this.props;

			this.forceFireTerrificAction('detailClosed');

			if (state === UiStates.Feed) {
				this.setState(
					{
						ui: UiStates.Feed,
					} as any,
					() => {
						this.props.doUnlockBody(LayoutTriggers.Card);
						this.forceFireTerrificAction('stopVideo');
					}
				);
			} else {
				this.setState(
					{
						ui: UiStates.Reopened,
					} as any,
					() => {
						this.props.doUnlockBody(LayoutTriggers.Card);
					}
				);
			}
		};

		public handleCloseOverlay = () => {
			if (this.state.ui !== UiStates.Detail) {
				return;
			}

			this.handleBackFromDetail();
		};

		public handleCloseOverlayArticle = (event) => {
			if (!$(event.target).hasClass('m-card')) {
				return;
			}

			this.handleCloseOverlay();
		};

		public handleClose = () => {
			if (this.state.ui === UiStates.Reopened) {
				this.setState({
					ui: UiStates.Folded,
				} as any);
			} else {
				this.setState({
					ui: UiStates.Detail,
				} as any);
			}
		};

		public handleCardContentClick = () => {
			if (this.state.ui === UiStates.Folded) {
				this.setState({
					ui: UiStates.Reopened,
				} as any);
			}
		};

		public handleScrollToCard = () => {
			if (this.state.ui === UiStates.Detail) {
				delayed(() => {
					this.forceFireTerrificAction('scrollToCard');
				}, 400);
				return;
			}

			this.forceFireTerrificAction('scrollToCard');
		};

		public handleShowDetailsClick = () => {
			this.setState({
				ui: UiStates.Detail,
			} as any);

			this.props.doTriggerTutorial(TUTORIAL_ID.CARD_DETAIL, 500, {
				cardType: this.props.type,
			});

			this.forceFireTerrificAction('detailOpened');
			this.props.doLockBody(LayoutTriggers.Card);
			this.props.trackClickShowDetail();
		};

		public handleSetCardUIFolded = () => {
			this.setState({ ui: UiStates.Folded } as any);
		};

		public handleCompleteCard = (callback: Function = noop) => {
			if (this.state.ui === UiStates.Detail) {
				this.handleCloseOverlay();
				delayed(() => {
					this.handleScrollToCard();
					callback();
				}, 500);
				return;
			}
			this.handleScrollToCard();
			callback();
		};

		public render() {
			return (
				<WrappedComponent
					childComponentRef={(ref: any) => (this.childComponent = ref)}
					uiState={this.state.ui}
					onBackFromDetail={this.handleBackFromDetail}
					onClose={this.handleClose}
					onCloseOverlay={this.handleCloseOverlay}
					onCloseOverlayArticle={this.handleCloseOverlayArticle}
					onCardContentClick={this.handleCardContentClick}
					onScrollToCard={this.handleScrollToCard}
					onShowDetailsClick={this.handleShowDetailsClick}
					onSetCardUIFolded={this.handleSetCardUIFolded}
					onCompleteCard={this.handleCompleteCard}
					{...this.props}
				/>
			);
		}
	};
}
